import { useEffect } from "react";
import { X } from "lucide-react";

/**
 * A right-anchored overlay for inspecting one record without leaving the list behind
 * it. No backdrop dim — same shadow-only separation the Combobox dropdown already
 * uses (input.tsx) — so this needed no new BRANDING token (§7) for a scrim.
 */
export function Drawer({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Transparent click-catcher: closes the drawer without dimming the table. */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="fixed inset-0 z-30 cursor-default"
      />
      <div
        role="dialog"
        aria-modal="true"
        className="border-bank-border bg-bank-surface fixed inset-y-0 right-0 z-40 flex w-full max-w-xl flex-col border-l shadow-2xl"
      >
        <div className="border-bank-border bg-bank-surface-2 flex items-center gap-3 border-b px-4 py-2.5">
          <h2 className="text-sm font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-bank-muted hover:bg-bank-surface-3 ml-auto rounded-md p-1"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto">{children}</div>
      </div>
    </>
  );
}
